import { UserManager, WebStorageStateStore } from 'oidc-client-ts'
import {
  clearActiveGuarantorLogoUrl,
  clearActiveGuarantorUrl,
  consumeGuarantorLogoutUrl,
  getActiveGuarantorLogoUrl,
  getActiveGuarantorUrl,
  normalizeGuarantorUrl,
  prepareGuarantorLogout,
  setActiveGuarantorLogoUrl,
  setActiveGuarantorUrl
} from './guarantorContext'

const authority = process.env.VUE_APP_AUTHORITY || 'https://auth.garantepay.net.br'
const clientId = process.env.VUE_APP_OIDC_CLIENT_ID || '7b4d1e9b-c3cd-4b8b-8c34-e50914bea1da'
const scope = process.env.VUE_APP_OIDC_SCOPE || 'openid profile email roles process_api'
const redirectPath = process.env.VUE_APP_OIDC_REDIRECT_PATH || '/callback'
const postLogoutPath = process.env.VUE_APP_OIDC_POST_LOGOUT_PATH || '/'
const redirectUri = process.env.VUE_APP_OIDC_REDIRECT_URI || `${getOrigin()}${redirectPath}`
const postLogoutRedirectUri = process.env.VUE_APP_OIDC_POST_LOGOUT_REDIRECT_URI || `${getOrigin()}${postLogoutPath}`

function getOrigin () {
  return window.location.origin
}

const userManager = new UserManager({
  authority,
  client_id: clientId,
  redirect_uri: redirectUri,
  post_logout_redirect_uri: postLogoutRedirectUri,
  response_type: 'code',
  scope,
  loadUserInfo: false,
  monitorSession: false,
  automaticSilentRenew: false,
  revokeTokensOnSignout: false,
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  stateStore: new WebStorageStateStore({ store: window.sessionStorage })
})

async function getUser () {
  return userManager.getUser()
}

async function isAuthenticated () {
  const user = await getUser()
  return Boolean(user && !user.expired && user.access_token)
}

async function getAccessToken () {
  const user = await getUser()
  return user && !user.expired ? user.access_token : null
}

async function signInRedirect (returnPath, options = {}) {
  const target = returnPath || `${window.location.pathname}${window.location.search}${window.location.hash}`
  const hasExplicitGuarantorUrl = Object.prototype.hasOwnProperty.call(options, 'urlGarantidora')
  const guarantorUrl = hasExplicitGuarantorUrl
    ? normalizeGuarantorUrl(options.urlGarantidora)
    : getActiveGuarantorUrl()
  const request = {
    state: {
      returnPath: target === '/' ? '/Home' : target,
      urlGarantidora: guarantorUrl
    }
  }

  if (guarantorUrl) {
    setActiveGuarantorUrl(guarantorUrl)
    await refreshActiveGuarantorBrand()
    request.extraQueryParams = { url_garantidora: guarantorUrl }
  } else {
    clearActiveGuarantorUrl()
  }

  return userManager.signinRedirect(request)
}

async function completeSignInRedirect () {
  const user = await userManager.signinRedirectCallback()
  const guarantorUrl = user?.profile?.UrlGarantidora ||
    user?.profile?.url_garantidora ||
    user?.state?.urlGarantidora
  setActiveGuarantorUrl(guarantorUrl)
  consumeGuarantorLogoutUrl()
  const returnPath = user?.state?.returnPath
  return typeof returnPath === 'string' && returnPath ? returnPath : '/Home'
}

async function refreshActiveGuarantorBrand () {
  const guarantorUrl = getActiveGuarantorUrl()
  if (!guarantorUrl) {
    clearActiveGuarantorLogoUrl()
    return null
  }

  try {
    const response = await fetch(
      `${authority.replace(/\/$/, '')}/login/context/${encodeURIComponent(guarantorUrl)}`,
      {
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      }
    )

    if (!response.ok) throw new Error(`Contexto da garantidora retornou HTTP ${response.status}.`)

    const context = await response.json()
    const contextUrl = normalizeGuarantorUrl(context?.urlGarantidora)
    if (!context?.isScoped || contextUrl !== guarantorUrl) {
      clearActiveGuarantorUrl()
      return null
    }

    return setActiveGuarantorLogoUrl(context?.logoGarantidora)
  } catch (error) {
    console.warn('[GarantePay] Não foi possível atualizar a identidade visual da garantidora.', error)
    return getActiveGuarantorLogoUrl()
  }
}

async function signOutRedirect () {
  const user = await getUser()
  const idToken = user?.id_token
  const guarantorUrl = prepareGuarantorLogout()
  localStorage.removeItem('_useravgar')
  sessionStorage.removeItem('avisoSistemaFechado')
  await userManager.removeUser()

  const request = {
    id_token_hint: idToken,
    post_logout_redirect_uri: postLogoutRedirectUri
  }

  if (guarantorUrl) {
    request.extraQueryParams = { url_garantidora: guarantorUrl }
  } else {
    clearActiveGuarantorUrl()
  }

  try {
    return await userManager.signoutRedirect(request)
  } catch (error) {
    const logoutUrl = new URL(`${authority}/connect/logout`)

    if (idToken) {
      logoutUrl.searchParams.set('id_token_hint', idToken)
    }

    if (guarantorUrl) {
      logoutUrl.searchParams.set('url_garantidora', guarantorUrl)
    }

    logoutUrl.searchParams.set('post_logout_redirect_uri', postLogoutRedirectUri)
    window.location.assign(logoutUrl.toString())
  }
}

async function clearAuthState () {
  await userManager.removeUser()
  localStorage.removeItem('_useravgar')
  consumeGuarantorLogoutUrl()
  clearActiveGuarantorUrl()
}

export default {
  authority,
  clientId,
  scope,
  getUser,
  isAuthenticated,
  getAccessToken,
  getActiveGuarantorLogoUrl,
  getActiveGuarantorUrl,
  consumeGuarantorLogoutUrl,
  normalizeGuarantorUrl,
  refreshActiveGuarantorBrand,
  clearActiveGuarantorLogoUrl,
  signInRedirect,
  completeSignInRedirect,
  signOutRedirect,
  clearAuthState
}
